import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import clientOnly from '../../components/clientOnly';

// Leaflet touches `window` at module scope, which crashes the build-time
// prerender of this route (react-dom/server has no window) — so both map
// sections load client-side only, AFTER mount. Server HTML and the first
// client render both show nothing here (hydration-identical); the maps pop
// in once their chunk arrives, exactly like a lazy route chunk. All the
// crawlable content on this page (city sections, FAQs, links) is plain
// JSX above/below the maps and prerenders fully.
const InteractiveServiceMap = clientOnly(() => import('./components/InteractiveServiceMap'));
const NationalFootprintMap = clientOnly(() => import('./components/NationalFootprintMap'));
import AddressChecker from '../../components/AddressChecker';
import { openCustomerPortal } from '../../utils/cn';

const ohioCities = [
  {
    city: 'Dayton',
    state: 'OH',
    headline: 'Dayton & Surrounding Areas',
    body: 'From Kettering to Centerville, we\'ve got the Miami Valley covered. Experience roof replacement without the wait or the pushy sales pitch.',
    link: '/service-areas/ohio/dayton',
    linkText: 'View Dayton Details',
  },
  {
    city: 'Columbus',
    state: 'OH',
    headline: 'Columbus & Central Ohio',
    body: 'Protecting homes across the Arch City. Use our SmartPick™ technology to design, price, and schedule your new roof from the comfort of your couch.',
    link: '/service-areas/ohio/columbus',
    linkText: 'View Columbus Details',
  },
  {
    city: 'Cincinnati',
    state: 'OH',
    headline: 'Cincinnati & Northern KY',
    body: 'Serving Greater Cincinnati with 100% transparent pricing and top-tier premium materials. Get your exact price in ~30 minutes.',
    link: '/service-areas/ohio/cincinnati',
    linkText: 'View Cincinnati Details',
  },
  {
    city: 'Dallas',
    state: 'TX',
    headline: 'Dallas & the DFW Metroplex',
    body: 'Our Texas launch market. GAF-exclusive roofs built for Hail Alley, open-book pricing, and $0 down — licensed in Texas with certified crews statewide.',
    link: '/service-areas/texas/dallas',
    linkText: 'View Dallas Details',
  },
];

const texasCities = [
  { city: 'Houston', state: 'TX' },
  { city: 'San Antonio', state: 'TX' },
  { city: 'Austin', state: 'TX' },
  { city: 'Fort Worth', state: 'TX' },
];

const nextUpCities = [
  { city: 'Louisville', state: 'KY' },
  { city: 'Lexington', state: 'KY' },
  { city: 'Pittsburgh', state: 'PA' },
  { city: 'Indianapolis', state: 'IN' },
  { city: 'Kansas City', state: 'MO' },
  { city: 'Denver', state: 'CO' },
  { city: 'Colorado Springs', state: 'CO' },
  { city: 'Omaha', state: 'NE' },
  { city: 'Washington', state: 'DC' },
  { city: 'Virginia Beach', state: 'VA' },
  { city: 'Charleston', state: 'SC' },
  { city: 'Philadelphia', state: 'PA' },
  { city: 'Atlanta', state: 'GA' },
];

const OhioCard = ({ city, state, headline, body, link, linkText }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={link}
      className="block group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="bg-white rounded-xl p-8 h-full flex flex-col transition-all duration-300 cursor-pointer"
        style={{
          borderRadius: '12px',
          boxShadow: hovered
            ? '0 20px 48px rgba(0,1,80,0.14), 0 4px 16px rgba(7,176,233,0.10)'
            : '0 4px 24px rgba(0,1,80,0.07), 0 1px 4px rgba(0,0,0,0.04)',
          transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        }}
      >
        {/* City badge */}
        <div className="flex items-center justify-between mb-4">
          <span
            className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
            style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
          >
            {city}, {state}
          </span>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300"
            style={{
              backgroundColor: hovered ? '#07B0E9' : '#F0F8FF',
            }}
          >
            <svg
              className="w-4 h-4 transition-all duration-300"
              style={{ color: hovered ? '#fff' : '#033494' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        <h3
          className="text-xl font-bold mb-3 leading-snug"
          style={{ color: '#000150', fontFamily: "'Poppins', 'Inter', sans-serif" }}
        >
          {headline}
        </h3>
        <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-6">{body}</p>

        <span
          className="inline-flex items-center gap-1.5 text-sm font-semibold transition-all duration-300"
          style={{ color: hovered ? '#07B0E9' : '#033494' }}
        >
          {linkText}
          <svg
            className="w-4 h-4 transition-transform duration-300"
            style={{ transform: hovered ? 'translateX(4px)' : 'translateX(0)' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </span>
      </div>
    </Link>
  );
};

const TexasCard = ({ city, state }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (email?.trim()) {
      setSubmitted(true);
    }
  };

  return (
    <div
      className="rounded-xl p-7 flex flex-col relative"
      style={{
        backgroundColor: '#F0F8FF',
        borderRadius: '12px',
        boxShadow: '0 4px 24px rgba(3,52,148,0.07), 0 1px 4px rgba(0,0,0,0.04)',
        border: '1px solid rgba(7,176,233,0.12)',
      }}
    >
      {/* Coming Soon badge */}
      <div className="absolute top-4 right-4">
        <span
          className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
          style={{ backgroundColor: '#033494', color: '#fff' }}
        >
          Coming Soon
        </span>
      </div>

      <div className="mb-4 mt-1">
        <span
          className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
          style={{ backgroundColor: 'rgba(7,176,233,0.12)', color: '#07B0E9' }}
        >
          {city}, {state}
        </span>
      </div>

      <h3
        className="text-lg font-bold mb-2"
        style={{ color: '#000150', fontFamily: "'Poppins', 'Inter', sans-serif" }}
      >
        {city} Area
      </h3>
      <p className="text-gray-500 text-sm mb-5 leading-relaxed flex-1">
        AI-powered roofing is coming to {city}. Be the first to know when we go live in your neighborhood.
      </p>

      {submitted ? (
        <div
          className="flex items-center gap-2 text-sm font-semibold py-2"
          style={{ color: '#033494' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          You're on the list!
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e?.target?.value)}
            placeholder="Enter your email address"
            required
            className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-full outline-none focus:border-blue-400 bg-white transition-colors duration-200"
            style={{ fontFamily: "'Inter', sans-serif" }}
          />
          <button
            type="submit"
            className="w-full py-2.5 text-sm font-bold rounded-full text-white transition-all duration-300 hover:opacity-90 hover:shadow-md"
            style={{ backgroundColor: '#033494', fontFamily: "'Poppins', 'Inter', sans-serif" }}
          >
            Notify Me
          </button>
        </form>
      )}
    </div>
  );
};

const NextUpCard = ({ city, state }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (email?.trim()) {
      setSubmitted(true);
    }
  };

  return (
    <div
      className="rounded-xl p-7 flex flex-col relative"
      style={{
        backgroundColor: '#FFFBEB',
        borderRadius: '12px',
        boxShadow: '0 4px 24px rgba(245,158,11,0.08), 0 1px 4px rgba(0,0,0,0.04)',
        border: '1px solid rgba(245,158,11,0.20)',
      }}
    >
      {/* Next Up badge */}
      <div className="absolute top-4 right-4">
        <span
          className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
          style={{ backgroundColor: '#F59E0B', color: '#fff' }}
        >
          Next 12 Months
        </span>
      </div>

      <div className="mb-4 mt-1">
        <span
          className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
          style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#D97706' }}
        >
          {city}, {state}
        </span>
      </div>

      <h3
        className="text-lg font-bold mb-2"
        style={{ color: '#000150', fontFamily: "'Poppins', 'Inter', sans-serif" }}
      >
        {city} Area
      </h3>
      <p className="text-gray-500 text-sm mb-5 leading-relaxed flex-1">
        OpenbooQ is coming to {city} in the next 12 months. Join the waitlist to be among the first homeowners to get an AI-powered roof price in 30 minutes in your area.
      </p>

      {submitted ? (
        <div
          className="flex items-center gap-2 text-sm font-semibold py-2"
          style={{ color: '#D97706' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          You're on the list!
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e?.target?.value)}
            placeholder="Enter your email address"
            required
            className="w-full px-4 py-2.5 text-sm border border-amber-200 rounded-full outline-none focus:border-amber-400 bg-white transition-colors duration-200"
            style={{ fontFamily: "'Inter', sans-serif" }}
          />
          <button
            type="submit"
            className="w-full py-2.5 text-sm font-bold rounded-full text-white transition-all duration-300 hover:opacity-90 hover:shadow-md"
            style={{ backgroundColor: '#F59E0B', fontFamily: "'Poppins', 'Inter', sans-serif" }}
          >
            Join Waitlist
          </button>
        </form>
      )}
    </div>
  );
};

const ServiceAreasPage = () => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["HomeAndConstructionBusiness", "RoofingContractor"],
    "@id": "https://openbooq.com/#localbusiness",
    "name": "OpenbooQ",
    "url": "https://openbooq.com",
    "description": "National online roofing platform serving homeowners across the United States with transparent roof replacement pricing and certified local installers.",
    "areaServed": [
      {
        "@type": "State",
        "name": "Ohio",
        "containsPlace": [
          { "@type": "City", "name": "Dayton" },
          { "@type": "City", "name": "Columbus" },
          { "@type": "City", "name": "Cincinnati" }
        ]
      },
      {
        "@type": "State",
        "name": "Texas",
        "containsPlace": [
          { "@type": "City", "name": "Dallas" },
          { "@type": "City", "name": "Houston" },
          { "@type": "City", "name": "San Antonio" },
          { "@type": "City", "name": "Austin" }
        ]
      },
      { "@type": "State", "name": "Kentucky" },
      { "@type": "State", "name": "Pennsylvania" },
      { "@type": "State", "name": "Indiana" },
      { "@type": "State", "name": "Missouri" },
      { "@type": "State", "name": "Colorado" }
    ],
    "serviceType": "Roof Replacement",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Roofing Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Roof Replacement",
            "description": "Full roof replacement with premium shingles and certified local installers"
          }
        }
      ]
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://openbooq.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Service Areas",
        "item": "https://openbooq.com/service-areas"
      }
    ]
  };

  const [address, setAddress] = useState('');

  const handleEstimate = (e) => {
    e?.preventDefault();
    openCustomerPortal();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA', fontFamily: "'Poppins', 'Inter', 'Montserrat', sans-serif" }}>
      <Helmet>
        <title>OpenbooQ Service Areas | Exact Online Roofing Prices</title>
        <meta
          name="description"
          content="Check if OpenbooQ is replacing roofs in your neighborhood. Get a hassle-free, AI-powered roof price in minutes. Currently serving Dayton, Columbus, Cincinnati and Dallas — and expanding nationwide."
        />
        <meta
          name="keywords"
          content="online roof estimate, AI roofing calculator, OpenbooQ service areas, roof replacement, buy roof online, modern roofing company"
        />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <Header />
      {/* Hero Section */}
      <section
        className="relative flex flex-col items-center justify-center text-center px-4 pt-40 pb-24 overflow-hidden"
        style={{
          backgroundImage: 'url(/assets/images/Gemini_Generated_Image_7vspn57vspn57vsp-1773072385704.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '520px',
        }}
      >
        {/* Dark overlay for text readability */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: 'rgba(0,0,21,0.72)' }}
          aria-hidden="true"
        />
        {/* Subtle geometric grid background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <svg
            className="absolute inset-0 w-full h-full opacity-[0.06]"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#07B0E9" strokeWidth="0.8" />
              </pattern>
              <pattern id="diag" width="120" height="120" patternUnits="userSpaceOnUse">
                <polygon points="60,10 110,50 90,110 30,110 10,50" fill="none" stroke="#07B0E9" strokeWidth="0.6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
            <rect width="100%" height="100%" fill="url(#diag)" opacity="0.4" />
          </svg>
        </div>

        {/* Glow accent */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(7,176,233,0.12) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-3xl mx-auto">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
            style={{ backgroundColor: 'rgba(7,176,233,0.15)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.3)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            OpenbooQ Service Areas
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-5 leading-tight"
            style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
          >
            Find Your Next Roof,
            <br />
            <span style={{ color: '#07B0E9' }}>Entirely Online.</span>
          </h1>

          <p className="text-gray-300 text-lg sm:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
            We're modernizing roof replacement across the country and expanding fast. Select your city to get started with a free, 30-minute SmartPick™ price.{' '}
            <span className="text-gray-400">No sales visits, no hidden fees, no pressure.</span>
          </p>

          {/* Address input */}
          <AddressChecker
            className="max-w-xl mx-auto"
            inputClassName=""
          />
        </div>
      </section>
      {/* Interactive Coverage Map */}
      <InteractiveServiceMap />
      <NationalFootprintMap />
      {/* Active Service Areas - Ohio */}
      <section className="py-20 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Active Installations
            </span>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              Where We're Currently Installing
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              We are proud to offer our fully remote, AI-powered roofing experience to homeowners in our active service areas. Click your city to learn more about local installations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {ohioCities?.map((c) => (
              <OhioCard key={c?.city} {...c} />
            ))}
          </div>
        </div>
      </section>
      {/* Coming Soon - Texas */}
      <section className="py-20 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4"
              style={{ backgroundColor: 'rgba(3,52,148,0.08)', color: '#033494' }}
            >
              Now in Texas
            </span>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#033494', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              The Lone Star State: Dallas Is Live
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              We're licensed in Texas with crews statewide — and{' '}
              <Link to="/service-areas/texas/dallas" className="font-semibold hover:underline" style={{ color: '#033494' }}>
                Dallas is live now
              </Link>
              . Don't see your city active yet? Join the waitlist to be the first to know when our AI estimating tool goes live in your neighborhood.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {texasCities?.map((c) => (
              <TexasCard key={c?.city} {...c} />
            ))}
          </div>
        </div>
      </section>
      {/* Next Up Cities */}
      <section className="py-20 px-4" style={{ backgroundColor: '#FAFAF5' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4"
              style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#D97706' }}
            >
              Next 12 Months
            </span>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              Next 12 Months: Expanding Nationwide
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              These cities are next on our expansion roadmap. Get on the waitlist now and be the first to experience AI-powered, fully transparent roofing in your neighborhood.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-12">
            {nextUpCities?.map((c) => (
              <NextUpCard key={c?.city + c?.state} {...c} />
            ))}
          </div>
        </div>
      </section>
      {/* Bottom CTA Banner */}
      <section
        className="py-16 px-4 text-center"
        style={{ backgroundColor: '#000150' }}
      >
        <div className="max-w-2xl mx-auto">
          <h3
            className="text-2xl sm:text-3xl font-black text-white mb-4"
            style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif" }}
          >
            Ready to get your roof price in 30 minutes?
          </h3>
          <p className="text-gray-400 mb-8 text-base">
            No sales calls. No pressure. Just your exact price in minutes.
          </p>
          <button
            id="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea-cta"
            onClick={() => {
              openCustomerPortal();
            }}
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full text-white font-bold text-base transition-all duration-300 hover:opacity-90 hover:scale-105 hover:shadow-2xl"
            style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 24px rgba(7,176,233,0.35)' }}
          >
            Get Roof Price
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>
      </section>

      {/* SEO Body Copy Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Roofing Services Nationwide — Transparent Pricing, Local Installers</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-5">
            OpenbooQ currently serves homeowners in the Greater Dayton, Columbus, and Cincinnati metro areas — plus Dallas and the DFW Metroplex, our Texas launch market — with rapid expansion planned across the country. Our service area model is built around local certified installer networks, meaning every roof replacement is handled by a vetted professional who knows your region's weather patterns, building codes, and material requirements.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            Homeowners across the country face unique roofing challenges: harsh winters, freeze-thaw cycles, spring hailstorms, and humid summers all take a toll on asphalt shingles. That's why OpenbooQ partners exclusively with installers who are trained on proper ice and water shield installation, adequate attic ventilation, and the specific installation requirements for each shingle product we carry.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Dayton, Ohio Roofing</h3>
              <p className="text-gray-600 leading-relaxed">
                Serving the Miami Valley including Kettering, Centerville, Beavercreek, Huber Heights, Miamisburg, Springboro, and surrounding communities. Dayton homeowners benefit from our transparent pricing model and fast installation scheduling — most projects are completed within 1–2 weeks of price approval.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Columbus, Ohio Roofing</h3>
              <p className="text-gray-600 leading-relaxed">
                Covering Central Ohio including Dublin, Westerville, Gahanna, Grove City, Hilliard, and New Albany. Columbus homeowners can use OpenbooQ's SmartPick™ technology to design, price, and schedule their roof replacement entirely online — no in-person visits required until installation day.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cincinnati, Ohio Roofing</h3>
              <p className="text-gray-600 leading-relaxed">
                Serving Greater Cincinnati and Northern Kentucky including Mason, Blue Ash, Hyde Park, Anderson Township, and Florence, KY. Our Cincinnati service area features the same transparent pricing and certified installer network available across all OpenbooQ markets.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Dallas, Texas Roofing</h3>
              <p className="text-gray-600 leading-relaxed">
                Our Texas launch market, covering Dallas neighborhoods from Oak Cliff and Lakewood to Preston Hollow, plus Richardson, Garland, Plano, Frisco, Arlington, and Fort Worth. OpenbooQ is licensed in Texas with GAF-certified crews statewide — every DFW roof is a full GAF system with open-book pricing, $0 down, and an exact quote texted and emailed in about 30 minutes. <Link to="/service-areas/texas/dallas" className="font-semibold hover:underline" style={{ color: '#033494' }}>See the Dallas service area</Link>.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Coming Soon: More of Texas & Beyond</h3>
              <p className="text-gray-600 leading-relaxed">
                We're actively expanding into Houston, San Antonio, Austin, and Fort Worth — with additional markets planned across the Midwest and Southeast. Sign up for early access to be notified when OpenbooQ launches in your city and receive priority scheduling for your roof replacement project.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceAreasPage;
